import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, ShieldCheck, CheckCircle2, AlertTriangle, MapPin, CalendarDays, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { depositsApi } from '../services/api';

const CARD_ELEMENT_OPTIONS = {
  // Default layout 'tabs' is more visible/robust than 'accordion' (accordion collapses when 1 method)
  layout: 'tabs',
  wallets: { applePay: 'auto', googlePay: 'auto' },
};

// Format YYYY-MM-DD → DD/MM/YYYY (French readable)
const formatDateFR = (iso) => {
  if (!iso || typeof iso !== 'string') return iso || '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

// Inner form (with access to Stripe context via useStripe / useElements)
const PaymentForm = ({ deposit, clientSecret, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!stripe || !elements) return;
    if (!cgvAccepted) {
      setError('Vous devez accepter les conditions générales.');
      return;
    }
    setSubmitting(true);

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: `${deposit.guest_first_name || ''} ${deposit.guest_last_name || ''}`.trim() || undefined,
              email: deposit.guest_email || undefined,
            },
          },
        },
        redirect: 'if_required',
      });

      if (result.error) {
        setError(result.error.message || 'Erreur de validation de la carte');
        setSubmitting(false);
        return;
      }

      // PaymentIntent should be in requires_capture status (manual capture flow)
      if (result.paymentIntent && (result.paymentIntent.status === 'requires_capture' || result.paymentIntent.status === 'succeeded')) {
        // Notify backend
        await depositsApi.confirm(deposit.token, {
          payment_intent_id: result.paymentIntent.id,
          cgv_accepted: true,
        });
        toast.success('Empreinte CB enregistrée');
        onSuccess();
      } else {
        setError(`Statut inattendu: ${result.paymentIntent?.status}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Moyen de paiement</Label>
        <div className="border-2 border-slate-200 rounded-xl p-4 bg-white focus-within:border-blue-500 transition-colors">
          <PaymentElement options={CARD_ELEMENT_OPTIONS} />
        </div>
        <p className="text-xs text-slate-500">
          Paiement sécurisé via Stripe — Apple Pay, Google Pay et cartes bancaires acceptés. Vos données ne sont jamais stockées par Ma Conciergerie Bretonne.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3 text-sm">
        <div className="flex items-start gap-2">
          <ShieldCheck className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">Comment ça marche ?</p>
            {deposit.mode === 'auth' ? (
              <ul className="text-amber-800 text-xs mt-1 space-y-1 list-disc list-inside">
                <li><strong>Aucune somme n'est bloquée</strong> sur votre carte pendant votre séjour.</li>
                <li>Une <strong>vérification de 1€ est effectuée puis immédiatement remboursée</strong> pour confirmer la validité de votre carte.</li>
                <li>Votre carte est <strong>enregistrée de manière sécurisée</strong> chez Stripe (norme PCI-DSS) jusqu'à 14 jours après votre départ.</li>
                <li>En cas de dégât constaté, un débit pourra être effectué sur cette carte, dans la limite de <strong>{deposit.amount_eur}€ maximum</strong>, en justifiant les frais.</li>
                <li>Sans dégât, votre carte est effacée automatiquement à l'issue des 14 jours.</li>
              </ul>
            ) : (
              <ul className="text-amber-800 text-xs mt-1 space-y-1 list-disc list-inside">
                <li>Une <strong>empreinte de {deposit.amount_eur}€</strong> est posée sur votre carte (l'argent n'est PAS débité).</li>
                <li>L'empreinte est conservée jusqu'à <strong>14 jours</strong> après votre départ.</li>
                <li>Sans dégât constaté, l'empreinte est automatiquement libérée. Aucun débit n'est effectué.</li>
                <li>En cas de dégât, seule la somme nécessaire (jusqu'à {deposit.amount_eur}€) sera prélevée.</li>
              </ul>
            )}
          </div>
        </div>
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={cgvAccepted}
          onChange={(e) => setCgvAccepted(e.target.checked)}
          className="mt-1 rounded"
          data-testid="cgv-checkbox"
        />
        <span className="text-sm text-slate-700">
          {deposit.mode === 'auth' ? (
            <>
              <strong>Conditions de caution</strong> : Je reconnais que ma carte bancaire sera enregistrée de manière sécurisée chez Stripe (norme PCI-DSS) pour la durée de mon séjour et jusqu'à 14 jours après mon départ.
              J'autorise expressément Ma Conciergerie Bretonne à débiter ma carte enregistrée, sans nouvelle saisie de ma part (paiement « off-session »), pour un montant total maximum de <strong>{deposit.amount_eur}€</strong>,
              uniquement en cas de dégât constaté, perte d'objet, fumée dans le logement, dépassement de durée ou tout manquement au règlement intérieur. Chaque débit sera justifié par écrit et limité au préjudice réel constaté.
              Une vérification de 1€ est effectuée maintenant puis remboursée immédiatement.
            </>
          ) : (
            <>
              <strong>Conditions de caution</strong> : empreinte CB de {deposit.amount_eur}€ maintenue jusqu'à 14 jours après mon départ.
              Prélèvement éventuel partiel ou total en cas de dégât constaté, perte d'objet, fumée, dépassement de durée ou tout manquement au règlement intérieur, dans la limite de {deposit.amount_eur}€.
              Sans dégât constaté, l'empreinte sera automatiquement libérée par ma banque. Conforme RGPD, données stockées de manière sécurisée chez Stripe (PCI-DSS).
            </>
          )}
        </span>
      </label>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || submitting || !cgvAccepted}
        className="w-full h-14 text-white text-base font-semibold rounded-xl shadow-lg hover:opacity-90 transition-opacity"
        style={{ background: 'linear-gradient(135deg, #0F1F3F 0%, #FF6B6B 100%)' }}
        data-testid="pay-deposit-btn"
      >
        {submitting ? (
          <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Validation en cours…</>
        ) : deposit.mode === 'auth' ? (
          <><ShieldCheck className="h-5 w-5 mr-2" /> Valider la caution (jusqu'à {deposit.amount_eur}€ max)</>
        ) : (
          <><ShieldCheck className="h-5 w-5 mr-2" /> Valider la caution de {deposit.amount_eur}€</>
        )}
      </Button>
    </form>
  );
};

export default function DepositPay() {
  const { token } = useParams();
  const [deposit, setDeposit] = useState(null);
  const [activeToken, setActiveToken] = useState(token);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [success, setSuccess] = useState(false);
  // Template form (only used when status === 'template')
  const [guestForm, setGuestForm] = useState({
    guest_first_name: '', guest_last_name: '', guest_email: '',
    property_name: '', property_address: '',
    arrival_date: '', departure_date: '',
  });
  const [startingFromTemplate, setStartingFromTemplate] = useState(false);

  const initSession = useCallback(async (tkn) => {
    // Load session data and create PI
    const { data: dep } = await depositsApi.getPublic(tkn);
    setDeposit(dep);

    if (dep.status === 'authorized' || dep.status === 'captured') {
      setSuccess(true);
      return;
    }
    if (dep.status !== 'awaiting_payment') {
      setError(`Ce lien n'est plus valide (statut: ${dep.status}).`);
      return;
    }
    const { data: pi } = await depositsApi.createPaymentIntent(tkn);
    if (!pi.publishable_key) {
      setError('Stripe n\'est pas configuré côté serveur. Veuillez contacter l\'administrateur.');
      return;
    }
    setClientSecret(pi.client_secret);
    setStripePromise(loadStripe(pi.publishable_key));
  }, []);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const { data: dep } = await depositsApi.getPublic(token);
      setDeposit(dep);
      if (dep.kind === 'template' || dep.status === 'template') {
        // Permanent link: wait for voyageur to fill form
        setLoading(false);
        return;
      }
      await initSession(token);
    } catch (e) {
      setError(e.response?.data?.detail || 'Lien invalide');
    } finally {
      setLoading(false);
    }
  }, [token, initSession]);

  useEffect(() => { init(); }, [init]);

  const handleStartFromTemplate = async (e) => {
    e.preventDefault();
    const required = ['guest_first_name', 'guest_last_name', 'arrival_date', 'departure_date'];
    if (deposit && !deposit.property_address) required.push('property_address');
    const missing = required.filter(k => !guestForm[k]?.trim());
    if (missing.length > 0) {
      toast.error('Merci de remplir tous les champs obligatoires');
      return;
    }
    setStartingFromTemplate(true);
    try {
      const { data } = await depositsApi.start(token, {
        ...guestForm,
        guest_email: guestForm.guest_email || null,
        property_name: guestForm.property_name || null,
        property_address: guestForm.property_address || null,
      });
      // Now load the child session
      setActiveToken(data.token);
      await initSession(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du démarrage');
    } finally {
      setStartingFromTemplate(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Lien indisponible</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success && deposit) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #fff5f5 0%, #f0f4ff 100%)' }}>
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <img src="/logo-playmcb-caution.png" alt="PlayMCB Caution" className="h-20 mx-auto mb-3" onError={(e) => { e.target.style.display = 'none'; }} />
            <CheckCircle2 className="h-14 w-14 mx-auto mb-3" style={{ color: '#FF6B6B' }} />
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#0F1F3F' }}>Caution validée ✓</h2>
            <p className="text-slate-600 mb-4">
              {deposit.mode === 'auth' ? (
                <>Votre carte a été <strong>enregistrée de manière sécurisée</strong>. <br/>Aucune somme n'est bloquée sur votre compte.</>
              ) : (
                <>Votre empreinte CB de <strong>{deposit.amount_eur}€</strong> a bien été enregistrée.</>
              )}
            </p>
            <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-1 text-left">
              <p><strong>Séjour :</strong> du {formatDateFR(deposit.arrival_date)} au {formatDateFR(deposit.departure_date)}</p>
              {deposit.card_last4 && <p><strong>Carte :</strong> {deposit.card_brand?.toUpperCase()} •••• {deposit.card_last4}</p>}
              {deposit.mode === 'auth' && <p className="text-emerald-700"><strong>Plafond max débitable :</strong> {deposit.amount_eur} €</p>}
            </div>
            <p className="text-xs text-slate-500 mt-4">
              {deposit.mode === 'auth'
                ? "Aucune somme n'a été bloquée sur votre carte. Un débit ne sera effectué qu'en cas de dégât constaté et justifié, dans la limite du plafond ci-dessus."
                : "Aucune somme n'a été débitée. L'empreinte sera libérée automatiquement sous 14 jours, sauf en cas de dégât constaté."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-rose-50 py-6 px-4">
      <div className="max-w-md mx-auto">
        {/* Header / brand */}
        <div className="text-center mb-5">
          <img src="/logo-playmcb-caution.png" alt="PlayMCB Caution" className="h-32 mx-auto mb-2" onError={(e) => { e.target.style.display = 'none'; }} />
          <p className="text-sm text-slate-500">Empreinte sécurisée, sans débit immédiat</p>
        </div>

        {/* Trust badge */}
        <div className="flex items-center justify-center gap-2 text-white py-2.5 px-4 rounded-full shadow-md mb-5 max-w-xs mx-auto" style={{ backgroundColor: '#0F1F3F' }}>
          <Lock className="h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
          <span className="text-sm font-semibold tracking-tight">Paiement 100% sécurisé · Crypté SSL</span>
        </div>

        {/* Recap */}
        {deposit && (
          <Card className="border-0 shadow-md mb-4">
            <CardContent className="p-5 space-y-3">
              {deposit.kind === 'template' || deposit.status === 'template' ? (
                <>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{deposit.mode === 'auth' ? 'Montant maximum de la caution' : 'Montant de la caution'}</p>
                    <p className="text-3xl font-bold" style={{ color: '#0F1F3F' }}>{deposit.amount_eur} €</p>
                    {deposit.mode === 'auth' && <p className="text-xs text-emerald-700 mt-1">✨ Vérification 1€ remboursée. Aucun blocage sur votre compte.</p>}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Voyageur</p>
                    <p className="font-semibold text-slate-900">{deposit.guest_first_name} {deposit.guest_last_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Dates</p>
                    <p className="font-semibold text-slate-900">Du {formatDateFR(deposit.arrival_date)} au {formatDateFR(deposit.departure_date)}</p>
                  </div>
                  <div className="pt-3 border-t border-slate-200">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{deposit.mode === 'auth' ? 'Montant maximum de la caution' : 'Montant de la caution'}</p>
                    <p className="text-3xl font-bold" style={{ color: '#0F1F3F' }}>{deposit.amount_eur} €</p>
                    {deposit.mode === 'auth' && <p className="text-xs text-emerald-700 mt-1">✨ Vérification 1€ remboursée. Aucun blocage sur votre compte.</p>}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            {deposit && (deposit.kind === 'template' || deposit.status === 'template') ? (
              <form onSubmit={handleStartFromTemplate} className="space-y-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 mb-1">Vos informations</h2>
                  <p className="text-xs text-slate-500">Merci de renseigner vos coordonnées pour finaliser la caution.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Prénom *</Label>
                    <input className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-base focus:border-blue-500 outline-none bg-white min-h-[48px]" value={guestForm.guest_first_name} onChange={(e) => setGuestForm({ ...guestForm, guest_first_name: e.target.value })} required />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nom *</Label>
                    <input className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-base focus:border-blue-500 outline-none bg-white min-h-[48px]" value={guestForm.guest_last_name} onChange={(e) => setGuestForm({ ...guestForm, guest_last_name: e.target.value })} required />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email (optionnel)</Label>
                  <input type="email" className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-base focus:border-blue-500 outline-none bg-white min-h-[48px]" value={guestForm.guest_email} onChange={(e) => setGuestForm({ ...guestForm, guest_email: e.target.value })} />
                </div>
                {!deposit.property_address && (
                  <div className="space-y-1">
                    <Label className="text-xs">Adresse du logement *</Label>
                    <input className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-base focus:border-blue-500 outline-none bg-white min-h-[48px]" value={guestForm.property_address} onChange={(e) => setGuestForm({ ...guestForm, property_address: e.target.value })} placeholder="12 rue de la Plage, 22240 Plouha" required />
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Date d'arrivée *</Label>
                    <input
                      type="date"
                      className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-base focus:border-blue-500 outline-none bg-white text-slate-900 min-h-[48px]"
                      style={{ WebkitAppearance: 'none', appearance: 'none' }}
                      value={guestForm.arrival_date}
                      onChange={(e) => setGuestForm({ ...guestForm, arrival_date: e.target.value })}
                      required
                      data-testid="guest-arrival"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Date de départ *</Label>
                    <input
                      type="date"
                      className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-base focus:border-blue-500 outline-none bg-white text-slate-900 min-h-[48px]"
                      style={{ WebkitAppearance: 'none', appearance: 'none' }}
                      value={guestForm.departure_date}
                      onChange={(e) => setGuestForm({ ...guestForm, departure_date: e.target.value })}
                      required
                      data-testid="guest-departure"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={startingFromTemplate}
                  className="w-full h-12 text-white text-base font-semibold rounded-xl shadow-lg hover:opacity-90 transition-opacity"
                  style={{ background: 'linear-gradient(135deg, #0F1F3F 0%, #FF6B6B 100%)' }}
                >
                  {startingFromTemplate ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Patientez…</> : 'Continuer'}
                </Button>
              </form>
            ) : stripePromise && clientSecret && deposit ? (
              <Elements stripe={stripePromise} options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#FF6B6B',
                    colorBackground: '#ffffff',
                    colorText: '#0F1F3F',
                    colorDanger: '#ef4444',
                    fontFamily: 'Figtree, system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '10px',
                  },
                },
              }}>
                <PaymentForm deposit={deposit} clientSecret={clientSecret} onSuccess={() => setSuccess(true)} />
              </Elements>
            ) : (
              <div className="text-center text-slate-500 py-6">
                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trust footer */}
        <div className="mt-5 space-y-3">
          {/* Card networks accepted */}
          <div className="flex items-center justify-center gap-3 text-slate-400">
            <span className="text-xs font-medium">Cartes acceptées :</span>
            <svg viewBox="0 0 48 32" className="h-6" aria-label="Visa"><rect width="48" height="32" rx="4" fill="#1A1F71"/><text x="24" y="22" textAnchor="middle" fill="white" fontSize="14" fontWeight="900" fontFamily="Arial, sans-serif">VISA</text></svg>
            <svg viewBox="0 0 48 32" className="h-6" aria-label="Mastercard"><rect width="48" height="32" rx="4" fill="white" stroke="#e2e8f0"/><circle cx="20" cy="16" r="8" fill="#EB001B"/><circle cx="28" cy="16" r="8" fill="#F79E1B" fillOpacity="0.85"/></svg>
            <svg viewBox="0 0 48 32" className="h-6" aria-label="Amex"><rect width="48" height="32" rx="4" fill="#006FCF"/><text x="24" y="20" textAnchor="middle" fill="white" fontSize="9" fontWeight="900" fontFamily="Arial, sans-serif">AMEX</text></svg>
          </div>

          {/* Powered by Stripe */}
          <div className="flex flex-col items-center gap-1">
            <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 transition-colors">
              <span className="text-[11px] uppercase tracking-wider">Powered by</span>
              <svg viewBox="0 0 60 25" className="h-4" aria-label="Stripe">
                <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66-1.42 0-2.97-.27-4.5-.95v-3.92c1.38.75 3.14 1.31 4.51 1.31.92 0 1.58-.24 1.58-1C6.32 13.83 0 14.83 0 10.6 0 7.5 2.18 5.65 5.52 5.65c1.36 0 2.72.21 4.09.74v3.87a9.32 9.32 0 0 0-4.1-1.06c-.86 0-1.4.25-1.4.9 0 1.85 6.35.97 6.35 5.54z" fill="#635BFF" fillRule="evenodd"/>
              </svg>
            </a>
            <p className="text-[10px] text-slate-400">Conforme PCI-DSS · Cryptage SSL 256 bits</p>
          </div>
        </div>
      </div>
    </div>
  );
}
